import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiX,
  FiImage,
  FiChevronLeft,
  FiSearch,
  FiDownload,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getSupportIncome } from "../../store/slices/withdraw";

const SupportIncome = () => {
  const dispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState(null);

  const {
    supportIncome = [],
    supportIncomeLoading,
    supportIncomeError,
    supportIncomePagination = {},
  } = useSelector((state) => state.withdraw);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  /* =====================================================
     PAGINATION
  ===================================================== */

  const totalIncome =
    Number(supportIncomePagination?.totalRecords) || 0;

  const totalPages =
    Number(supportIncomePagination?.totalPages) ||
    (totalIncome > 0
      ? Math.ceil(totalIncome / limit)
      : 0);

  const currentPage =
    Number(supportIncomePagination?.currentPage) || page;

  const hasNextPage =
    supportIncomePagination?.hasNextPage ??
    currentPage < totalPages;

  const hasPreviousPage =
    supportIncomePagination?.hasPreviousPage ??
    currentPage > 1;

  /* =====================================================
     FETCH SUPPORT INCOME
  ===================================================== */

  useEffect(() => {
    dispatch(
      getSupportIncome({
        page,
        limit,
        search: search.trim(),
      })
    );
  }, [dispatch, page, limit, search]);

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* =====================================================
     FORMAT DATE TIME
  ===================================================== */

  const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* =====================================================
     CLOSE IMAGE VIEWER
  ===================================================== */

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  /* =====================================================
     PAGINATION HANDLERS
  ===================================================== */

  const handlePrevious = () => {
    if (hasPreviousPage) {
      setPage((prev) => Math.max(prev - 1, 1));
    }
  };

  const handleNext = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page
    ) {
      setPage(pageNumber);
    }
  };

  /* =====================================================
     PAGE NUMBERS
  ===================================================== */

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  /* =====================================================
     DISPLAY RANGE
  ===================================================== */

  const displayStart =
    totalIncome > 0
      ? (currentPage - 1) * limit + 1
      : 0;

  const displayEnd =
    totalIncome > 0
      ? Math.min(currentPage * limit, totalIncome)
      : 0;

  /* =====================================================
     DOWNLOAD PDF
  ===================================================== */

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const response = await dispatch(
        getSupportIncome({
          page: 1,
          limit: 100000,
          search: search.trim(),
        })
      ).unwrap();

      /*
       API RESPONSE:

       {
         status: 1,
         data: {
           records: [],
           pagination: {},
           totalAmount: 1535
         }
       }
      */

      const exportList =
        response?.data?.records ||
        response?.records ||
        [];

      if (!exportList.length) {
        alert("No support income records available.");
        return;
      }

      const doc = new jsPDF(
        "landscape",
        "mm",
        "a4"
      );

      doc.setFontSize(16);

      doc.text(
        "Support Income",
        14,
        15
      );

      if (search.trim()) {
        doc.setFontSize(9);

        doc.text(
          `Search: ${search.trim()}`,
          14,
          21
        );
      }

      const tableData = exportList.map(
        (item, index) => {
          const user =
            item?.userId || {};

          const amount =
            Number(item?.amount || 0);

          return [
            index + 1,

            user?.name || "-",

            user?.userId || "-",

            user?.email || "-",

            user?.mobileNo || "-",

            `$ ${amount.toLocaleString(
              "en-IN",
              {
                maximumFractionDigits: 2,
              }
            )}`,

            item?.remarks || "-",

            formatDate(item?.createdAt),
          ];
        }
      );

      autoTable(doc, {
        startY: search.trim()
          ? 27
          : 22,

        head: [[
          "#",
          "Name",
          "User ID",
          "Email",
          "Mobile",
          "Amount",
          "Remarks",
          "Date",
        ]],

        body: tableData,

        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 3,
        },

        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 12,
          },

          1: {
            cellWidth: 35,
          },

          2: {
            cellWidth: 30,
          },

          3: {
            cellWidth: 50,
          },

          4: {
            cellWidth: 30,
          },

          5: {
            cellWidth: 28,
          },

          6: {
            cellWidth: 55,
          },

          7: {
            cellWidth: 30,
          },
        },
      });

      doc.save(
        "support-income.pdf"
      );
    } catch (error) {
      console.error(
        "PDF download failed:",
        error
      );

      alert(
        "Failed to download PDF."
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="gi-referral-page">

      {/* ================= BREADCRUMB ================= */}

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Support Income
        </span>

      </div>

      {/* ================= TABLE ================= */}

      <section className="gi-referral-table-card">

        {/* ================= FILTER BAR ================= */}

        <div className="gi-users-filter">

          {/* PER PAGE */}

          <div className="gi-users-page-size me-2">

            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                const newLimit =
                  Number(e.target.value);

                setLimit(newLimit);
                setPage(1);
              }}
              disabled={supportIncomeLoading}
            >
              <option value={10}>
                10
              </option>

              <option value={25}>
                25
              </option>

              <option value={50}>
                50
              </option>

              <option value={100}>
                100
              </option>
            </select>

            <span>
              per page
            </span>

          </div>

          {/* SEARCH */}

          <div className="gi-users-search me-2">

            <FiSearch />

            <input
              type="text"
              placeholder="Search support income..."
              value={search}
              onChange={(e) => {
                setSearch(
                  e.target.value
                );
                setPage(1);
              }}
            />

          </div>

          {/* DOWNLOAD */}

          <button
            type="button"
            className="gi-user-export-btn"
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              supportIncomeLoading ||
              supportIncome.length === 0
            }
          >
            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>
          </button>

        </div>

        {/* ================= TABLE ================= */}

        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Name
                </th>

                <th className="text-center">
                  User ID
                </th>

                <th className="text-center">
                  Email
                </th>

                <th className="text-center">
                  Mobile
                </th>

                <th className="text-center">
                  Amount
                </th>

                <th className="text-center">
                  Remarks
                </th>

                <th className="text-center">
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {/* ================= LOADING ================= */}

              {supportIncomeLoading && (
                <tr>

                  <td
                    colSpan="8"
                    className="gi-table-message"
                  >
                    Loading support income...
                  </td>

                </tr>
              )}

              {/* ================= ERROR ================= */}

              {!supportIncomeLoading &&
                supportIncomeError && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      {supportIncomeError}
                    </td>

                  </tr>

                )}

              {/* ================= NO DATA ================= */}

              {!supportIncomeLoading &&
                !supportIncomeError &&
                supportIncome.length === 0 && (

                  <tr>

                    <td
                      colSpan="8"
                      className="gi-table-message"
                    >
                      No support income found
                    </td>

                  </tr>

                )}

              {/* ================= SUPPORT INCOME ================= */}

              {!supportIncomeLoading &&
                !supportIncomeError &&
                supportIncome.map(
                  (item, index) => {

                    const user =
                      item?.userId || {};

                    return (
                      <tr
                        key={
                          item?._id ||
                          index
                        }
                      >

                        {/* SERIAL */}

                        <td className="gi-referral-number">

                          {String(
                            (currentPage - 1) *
                            limit +
                            index +
                            1
                          ).padStart(2, "0")}

                        </td>

                        {/* USER */}

                        <td>

                          <div className="gi-user-cell">

                            {user?.name ||
                              "-"}

                          </div>

                        </td>

                        {/* USER ID */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.userId ||
                              "-"}

                          </span>

                        </td>

                        {/* EMAIL */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.email ||
                              "-"}

                          </span>

                        </td>

                        {/* MOBILE */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.mobileNo ||
                              "-"}

                          </span>

                        </td>

                        {/* AMOUNT */}

                        <td className="text-center">

                          <strong className="gi-payment-amount">

                            $
                            {" "}
                            {Number(
                              item?.amount ||
                              0
                            ).toLocaleString(
                              "en-IN",
                              {
                                maximumFractionDigits: 2,
                              }
                            )}

                          </strong>

                        </td>

                        {/* REMARKS */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {item?.remarks ||
                              "-"}

                          </span>

                        </td>

                        {/* DATE */}

                        <td className="text-center">

                          <span className="gi-date-text">

                            {formatDate(
                              item?.createdAt
                            )}

                          </span>

                        </td>

                      </tr>
                    );
                  }
                )}

            </tbody>

          </table>

          {/* ================= PAGINATION ================= */}

          {!supportIncomeLoading &&
            !supportIncomeError &&
            totalPages > 0 && (

              <div className="gi-users-pagination">

                {/* PAGINATION INFO */}

                <div className="gi-pagination-info">

                  Showing{" "}

                  <strong>
                    {displayStart}
                  </strong>

                  {" "}to{" "}

                  <strong>
                    {displayEnd}
                  </strong>

                  {" "}of{" "}

                  <strong>
                    {totalIncome}
                  </strong>

                  {" "}payments

                </div>

                {/* PAGINATION BUTTONS */}

                <div className="gi-pagination-buttons">

                  {/* PREVIOUS */}

                  <button
                    type="button"
                    className="gi-pagination-btn"
                    onClick={handlePrevious}
                    disabled={
                      !hasPreviousPage
                    }
                  >
                    <FiChevronLeft />

                    <span>
                      Prev
                    </span>
                  </button>

                  {/* PAGE NUMBERS */}

                  {getPageNumbers().map(
                    (
                      pageNumber,
                      index
                    ) => {

                      if (
                        pageNumber ===
                        "..."
                      ) {
                        return (
                          <span
                            key={`dots-${index}`}
                            className="gi-pagination-dots"
                          >
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          type="button"
                          key={pageNumber}
                          className={`gi-pagination-number ${currentPage ===
                            pageNumber
                            ? "active"
                            : ""
                            }`}
                          onClick={() =>
                            handlePageChange(
                              pageNumber
                            )
                          }
                        >
                          {pageNumber}
                        </button>
                      );
                    }
                  )}

                  {/* NEXT */}

                  <button
                    type="button"
                    className="gi-pagination-btn"
                    onClick={handleNext}
                    disabled={
                      !hasNextPage
                    }
                  >
                    <span>
                      Next
                    </span>

                    <FiChevronRight />
                  </button>

                </div>

              </div>
            )}

        </div>

      </section>

      {/* ================= IMAGE VIEWER ================= */}

      {selectedImage && (

        <div
          className="gi-image-viewer-overlay"
          onClick={closeImageViewer}
        >

          <div
            className="gi-image-viewer-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <button
              type="button"
              className="gi-image-viewer-close"
              onClick={closeImageViewer}
            >
              <FiX />
            </button>

            <div className="gi-image-viewer-header">

              <div className="gi-image-viewer-title">

                <div className="gi-image-viewer-icon">
                  <FiImage />
                </div>

                <div>

                  <h3>
                    Payment Proof
                  </h3>

                  <p>
                    {selectedImage.userName ||
                      "User"}
                  </p>

                </div>

              </div>

            </div>

            <div className="gi-image-viewer-body">

              <img
                src={selectedImage.url}
                alt="Payment Proof"
              />

            </div>

            <div className="gi-image-viewer-footer">

              <span>

                Transaction ID:

                <strong>
                  {selectedImage.transactionId ||
                    "-"}
                </strong>

              </span>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default SupportIncome;